/**
 * WB4-IX0 — Interaction Surface Contract SSOT.
 *
 * Extends the 7-path execution surface model with interaction-native surfaces:
 * `inline_edit` and `drag_drop`. These surfaces dispatch via ActionExecutor /
 * workbench-interaction-dispatch — never direct domain mutation from UI.
 */

import type { WorkbenchKernelExecutionSurface } from './action-types';
import { buildActionSafety } from './workbench-action-safety-policy';

/** Legacy 7-path surfaces (button … shortcut). */
export const WORKBENCH_KERNEL_LEGACY_EXECUTION_SURFACES = [
  'button',
  'context_menu',
  'inspector',
  'palette',
  'bulk',
  'ai',
  'shortcut',
] as const satisfies readonly WorkbenchKernelExecutionSurface[];

/** WB4-IX0 — interaction-native execution surfaces. */
export const WORKBENCH_KERNEL_INTERACTION_EXECUTION_SURFACES = [
  'inline_edit',
  'drag_drop',
] as const satisfies readonly WorkbenchKernelExecutionSurface[];

export const WORKBENCH_KERNEL_ALL_EXECUTION_SURFACES = [
  ...WORKBENCH_KERNEL_LEGACY_EXECUTION_SURFACES,
  ...WORKBENCH_KERNEL_INTERACTION_EXECUTION_SURFACES,
] as const satisfies readonly WorkbenchKernelExecutionSurface[];

export const INLINE_EDIT_ACTION_ID_PREFIX = 'inline_edit:' as const;
export const INLINE_UPDATE_HANDLER_PREFIX = 'inline:update:' as const;
export const INLINE_UNDO_HANDLER_PREFIX = 'inline:undo:' as const;

export const DRAG_DROP_ACTION_ID_PREFIX = 'drag_drop:' as const;
export const DRAG_DROP_HANDLER_PREFIX = 'drag:drop:' as const;

/** Reserved drag-drop actions — handler wiring deferred to WB4-DND1. */
export const INTERACTION_SURFACE_DRAG_DROP_RESERVED_ACTION_IDS = [
  'drag_drop:assign_task',
  'drag_drop:attach_evidence',
  'drag_drop:start_production_flow',
  'drag_drop:route_to_picking_queue',
  'drag_drop:route_to_collection_queue',
  'drag_drop:attach_approval_packet',
  'drag_drop:create_purchase_request',
  'drag_drop:link_document_to_order',
  'drag_drop:link_order_to_customer',
] as const;

export type InteractionSurfaceDragDropReservedActionId =
  (typeof INTERACTION_SURFACE_DRAG_DROP_RESERVED_ACTION_IDS)[number];

export interface WorkbenchInlineFieldMutation {
  fieldKey: string;
  fieldLabel: string;
  previousValue: string;
  nextValue: string;
}

export function isInteractionNativeExecutionSurface(
  surface: WorkbenchKernelExecutionSurface
): boolean {
  return (WORKBENCH_KERNEL_INTERACTION_EXECUTION_SURFACES as readonly string[]).includes(surface);
}

export function buildInlineEditActionId(fieldKey: string): string {
  return `${INLINE_EDIT_ACTION_ID_PREFIX}${fieldKey}`;
}

export function buildInlineUpdateHandlerRef(fieldKey: string): string {
  return `${INLINE_UPDATE_HANDLER_PREFIX}${fieldKey}`;
}

export function buildInlineUndoHandlerRef(fieldKey: string): string {
  return `${INLINE_UNDO_HANDLER_PREFIX}${fieldKey}`;
}

export function buildDragDropActionId(actionSlug: string): string {
  return `${DRAG_DROP_ACTION_ID_PREFIX}${actionSlug}`;
}

export function buildDragDropHandlerRef(actionSlug: string): string {
  return `${DRAG_DROP_HANDLER_PREFIX}${actionSlug}`;
}

export function isInlineUpdateHandlerRef(handlerRef: string): boolean {
  return handlerRef.startsWith(INLINE_UPDATE_HANDLER_PREFIX);
}

export function isInlineUndoHandlerRef(handlerRef: string): boolean {
  return handlerRef.startsWith(INLINE_UNDO_HANDLER_PREFIX);
}

export function isInlineFieldHandlerRef(handlerRef: string): boolean {
  return isInlineUpdateHandlerRef(handlerRef) || isInlineUndoHandlerRef(handlerRef);
}

export function isDragDropHandlerRef(handlerRef: string): boolean {
  return handlerRef.startsWith(DRAG_DROP_HANDLER_PREFIX);
}

export function isInteractionSurfaceHandlerRef(handlerRef: string): boolean {
  return isInlineFieldHandlerRef(handlerRef) || isDragDropHandlerRef(handlerRef);
}

export function parseInlineFieldKeyFromHandlerRef(handlerRef: string): string | undefined {
  if (isInlineUpdateHandlerRef(handlerRef)) {
    const key = handlerRef.slice(INLINE_UPDATE_HANDLER_PREFIX.length).trim();
    return key || undefined;
  }
  if (isInlineUndoHandlerRef(handlerRef)) {
    const key = handlerRef.slice(INLINE_UNDO_HANDLER_PREFIX.length).trim();
    return key || undefined;
  }
  return undefined;
}

/** Default low-risk inline edit safety — overridden per-field in IE1 registry. */
export function resolveDefaultInlineEditSafety() {
  return buildActionSafety('low', ['table-row'], {
    recoverabilityMode: 'toast_undo',
    confirmation: 'none',
  });
}
